import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import { useFeatureFlags } from 'helpers/feature-flag'
import { useFlagsStatus } from '@unleash/proxy-client-react'
// @mui
import { Check } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  stepConnectorClasses,
  styled,
} from '@mui/material'
// components
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs'
import Page from 'components/Page'
import useSettings from 'hooks/useSettings'
import { PATH_DASHBOARD, PATH_PAGE } from 'routes/paths'
import Layout from '~/layouts'
import BankAccountForm from 'domains/Users/Verification/containers/BankAccountForm'
import NpwpForm from 'domains/Users/Verification/containers/NpwpForm'
import isEmpty from 'helpers/is-empty'
import PreviewForm from 'domains/Users/Verification/containers/PreviewForm'
import VerificationDashboard from 'domains/Users/Verification/containers/VerificationDashboard'
import FreezePage from 'domains/Users/Verification/containers/VerificationDashboard/FreezePage'
import RejectedPage from 'domains/Users/Verification/containers/VerificationDashboard/RejectedPage'
import PendingPage from 'domains/Users/Verification/containers/VerificationDashboard/PendingPage'
import VerifiedPage from 'domains/Users/Verification/containers/VerificationDashboard/VerifiedPage'
// hooks
import useAuth from 'hooks/useAuth'
import { useUserKYC } from 'domains/Users/Verification/hooks/useVerificationUser'
import { useBoundVerificationUserStore } from 'domains/Users/Verification/stores'
import useCheckValidToken from 'helpers/token'
// constants
import { BankAccountStatus } from 'domains/Users/Verification/contants'
import dynamic from 'next/dynamic'
import trackEvent from '~/trackers'
import useLocales from 'hooks/useLocales'
import { FEATURE_FLAGS } from 'constants/feature-flags'

const PersonalDataForm = dynamic(
  () => import('../../containers/PersonalDataForm'),
)
const FillProfileDialog = dynamic(
  () => import('commons/containers/FillProfileDialog'),
)

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 3,
    borderRadius: 1,
  },
}))

const QontoStepIconRoot = styled('div')<{
  ownerState: { active?: boolean }
}>(({ theme, ownerState }) => ({
  color: theme.palette.divider,
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  '& .QontoStepIcon-completedIcon': {
    color: theme.palette.primary.main,
    zIndex: 1,
    fontSize: 24,
  },
  '& .QontoStepIcon-circle': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
}))

function QontoStepIcon(props: StepIconProps) {
  const { active, completed, className } = props

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  )
}

export default function UserVerification() {
  const { t } = useLocales()
  const { push, pathname, query, isReady } = useRouter()
  const { token } = query
  const { themeStretch } = useSettings()
  const { flagsReady } = useFlagsStatus()
  const { isAuthenticated, user, doUserAuthenticated } = useAuth()
  const { checkValidToken } = useCheckValidToken()
  const userKYCFlag = useFeatureFlags(FEATURE_FLAGS.userKyc)

  const [isEditingMode, setEditingMode] = useState(false)
  const [isOpenFillProfileDialog, setIsOpenFillProfileDialog] =
    useState(false)
  const trackerData = { pathname, query, user }
  const { personalData, activeStep, setActiveStep } =
    useBoundVerificationUserStore((state) => ({
      activeStep: state.activeStep,
      setActiveStep: state.setActiveStep,
      personalData: state.personalData,
    }))

  const { refetch: getUserKYC, isFetching: isFetchingKYC } =
    useUserKYC()

  const verificationType = [
    t('key_personal_data'),
    t('key_bank_account'),
    t('key_NPWP'),
    'Preview',
  ]
  const title = verificationType[activeStep]

  useEffect(() => {
    if (isAuthenticated && !userKYCFlag && flagsReady)
      push(PATH_PAGE.page404)
  }, [pathname, isAuthenticated, flagsReady])

  useEffect(() => {
    if (isAuthenticated && !isEmpty(user?.user_id)) {
      getUserKYC()
      if (
        activeStep !== null &&
        user?.creator?.creator_class === 'PERSONAL'
      ) {
        setIsOpenFillProfileDialog(true)
      }
    }
  }, [isAuthenticated, user, activeStep])

  useEffect(() => {
    if (!isEmpty(token)) checkValidToken()
  }, [token])

  useEffect(() => {
    if (isReady) {
      trackEvent.user('visit_kyc_verification', trackerData)
    }
  }, [isReady])

  if (isFetchingKYC) {
    return (
      <Layout>
        <Stack
          sx={{
            height: 'calc(100vh - 178px)',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Stack>
      </Layout>
    )
  }

  const bankAccountStatus = personalData?.bank_account_status

  if (
    bankAccountStatus === BankAccountStatus.UNVERIFIED ||
    (bankAccountStatus === BankAccountStatus.VERIFIED &&
      isEditingMode) ||
    (bankAccountStatus === BankAccountStatus.REJECTED &&
      isEditingMode)
  ) {
    if (activeStep === null) {
      return (
        <Layout>
          <Page title={t('key_data_verification')}>
            <Container maxWidth={themeStretch ? false : 'xl'}>
              <HeaderBreadcrumbs
                heading={t('key_data_verification')}
                links={[
                  { name: 'Dashboard', href: PATH_DASHBOARD.root },
                  {
                    name: t('key_data_verification'),
                  },
                ]}
              />
              <VerificationDashboard />
            </Container>
          </Page>
        </Layout>
      )
    }

    return (
      <Layout>
        <Page title={t('key_data_verification_input')}>
          <Container maxWidth={themeStretch ? false : 'xl'}>
            <HeaderBreadcrumbs
              heading={t('key_data_verification_input')}
              links={[
                { name: 'Dashboard', href: PATH_DASHBOARD.root },
                {
                  name: t('key_data_verification'),
                  href: PATH_DASHBOARD.general.verification.root,
                },
                {
                  name: title,
                },
              ]}
            />
            <Box
              sx={(theme) => ({
                [theme.breakpoints.up('md')]: {
                  maxWidth: '720px',
                },
                margin: '0px auto',
              })}
            >
              <Grid container sx={{ marginBottom: { md: '40px' } }}>
                <Grid item md={12}>
                  <Box
                    sx={(theme) => ({
                      [theme.breakpoints.down('md')]: {
                        display: 'none',
                      },
                    })}
                  >
                    <Stepper
                      alternativeLabel
                      activeStep={activeStep}
                      connector={<QontoConnector />}
                    >
                      {verificationType.map((step) => (
                        <Step key={step}>
                          <StepLabel
                            StepIconComponent={QontoStepIcon}
                          >
                            {step}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                </Grid>
              </Grid>
              {activeStep === 0 && (
                <PersonalDataForm setEditingMode={setEditingMode} />
              )}
              {activeStep === 1 && <BankAccountForm />}
              {activeStep === 2 && <NpwpForm />}
              {activeStep === 3 && <PreviewForm />}
              <FillProfileDialog
                isOpen={isOpenFillProfileDialog}
                onClose={() => {
                  setIsOpenFillProfileDialog(false)
                  setActiveStep(null)
                }}
                onSuccess={() => {
                  doUserAuthenticated()
                  setIsOpenFillProfileDialog(false)
                }}
              />
            </Box>
          </Container>
        </Page>
      </Layout>
    )
  }

  if (bankAccountStatus === BankAccountStatus.VERIFIED) {
    return <VerifiedPage />
  }

  if (bankAccountStatus === BankAccountStatus.PENDING) {
    return <PendingPage />
  }

  if (bankAccountStatus === BankAccountStatus.REJECTED) {
    return <RejectedPage setEditingMode={setEditingMode} />
  }

  if (bankAccountStatus === BankAccountStatus.FREEZE) {
    return <FreezePage />
  }

  return null
}
